import { useEffect, useLayoutEffect, useState } from "react";
import AppTabs, { TabItem } from "../../../../commons/components/Tabs";
import { useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import {
  AppAvatar,
  AppBorderBox,
  AppBox,
  AppBreadCrumbs,
  AppButton,
  AppIconButton,
  AppModal,
  AppPaper,
  AppSelect,
  AppSkeletonHeader,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import IconEdit from "../../../../commons/components/icons/edit";
import { SeasonStatusEnum } from "../../../../commons/enums/season-status-enum";
import { IFootballSeason } from "../../../../commons/models/football/interface/i-football-season";
import IconTrophy from "../../../../commons/components/icons/trophy";
import IconQuestionCircle from "../../../../commons/components/icons/question-circle";
import IconArrowSyncCircle from "../../../../commons/components/icons/arrow-sync-circle";
import { seasonEnd } from "../../../../redux/slices/football/admin/season/manage-season-end-slice";
import moment from "moment";
import { formatDateTime } from "../../../../commons/utilities/date-utils";
import setDocumentTitle from "../../../../commons/utilities/document-title";
import { cleanUpSeasonFixtureGroups } from "../../../../redux/slices/football/admin/season/fixture-group/manage-fixture-group-list-slice";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../commons/components/interface";
import { useSnackbar } from "../../../../redux/snackbarProvider";
import { cleanUpSeasonTeamGroups } from "../../../../redux/slices/football/admin/season/team-group/manage-team-group-list-slice";
import { TournamentTypeEnum } from "../../../../commons/enums/tournament-type-enum";
import { cleanUpCompetitionSeasonTeams } from "../../../../redux/slices/football/admin/competition/manage-competition-season-teams-slice";
import { ISelectOptions } from "../../../../commons/components/ui-components/Select";
import { IFootballSeasonOption } from "../../../../redux/interfaces/i-football-season-state";
import { SortOrderEnum } from "../../../../commons/enums/sort-order-enum";
import { fetchFootballManageSeasonList } from "../../../../redux/slices/football/admin/competition/manage-competition-season-slice";
import { findIndexByCondition } from "../../../../commons/utilities/array-utils";
import { setGlobalSeasonSelectedOption } from "../../../../redux/slices/global-season-select-slice";
import ability from "../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../commons/enums/role-policy-action.enum";
import { ManageFootballCompetition } from "../../../../commons/models/football/admin/manage-football-competition";
import { FootballMatchDetail } from "../../../../commons/models/football/football-match-detail";
import { ManageFootballTeam } from "../../../../commons/models/football/admin/manage-football-team";
import { fetchManageHostedCompetition } from "../../../../redux/slices/football/admin/team/hosted-competition/manage-team-hosted-competition-slice";
import SeasonAddEdit from "../../tournaments/components/SeasonAddEdit";
import SeasonEnd from "../../tournaments/components/SeasonEnd";
import SeasonEndInprogress from "../../tournaments/components/SeasonEndInprogress";
import { BreadCrumbItem } from "../../../../commons/components/BreadCrumbs";

interface HostedTournamentManageDetailHeaderProps {
  onEditTournament?: (event?: Event) => void;
}

export default function HostedTournamentManageDetailHeader({
  onEditTournament,
}: HostedTournamentManageDetailHeaderProps) {
  const [currentTab, setCurrentTab] = useState("");
  const [activeItem, setActiveItem] = useState<TabItem>();
  const [crumbs, setCrumbs] = useState<BreadCrumbItem[]>([]);
  const [isSeasonStartEdit, setSeasonStartEdit] = useState(false);
  const [isSeasonEnd, setSeasonEnd] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<IFootballSeason | null>(null);
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const location = useLocation();
  const { hostedCompetitionInfo, hostedCompetitionInfoStatus } = useAppSelector(
    (state) => state.footballManageHostedCompetitionDetail
  );
  const { seasonList } = useAppSelector((state) => state.footballManageCompetitionSeason);
  const { seasonEndResponseStatus } = useAppSelector((state) => state.footballManageSeasonEnd);
  const [seasonOptions, setSeasonOptions] = useState<ISelectOptions[]>([]);
  const [currentSeasonOption, setCurrentSeasonOption] = useState<ISelectOptions>();
  const { selectedGlobalSeasonOption } = useAppSelector((state) => state.globalSeasonSelect);

  useEffect(() => {
    if (hostedCompetitionInfo.id) {
      setCrumbs([
        {
          label: hostedCompetitionInfo.hostTeam.name,
          href: `/manage/teams/${hostedCompetitionInfo.hostTeam.id}/hosted-tournaments`,
        },
        {
          label: hostedCompetitionInfo.title,
        },
      ]);
      const options: IFootballSeasonOption = {
        competitionId: hostedCompetitionInfo.id,
        urlParams: {
          hostTeamId: hostedCompetitionInfo.hostTeam.id,
          sort: "startAt",
          sortOrder: SortOrderEnum.Descending,
        },
      };
      dispatch(fetchFootballManageSeasonList(options));
    }
  }, [hostedCompetitionInfo]);

  useLayoutEffect(() => {
    if (seasonList.data.length > 0) {
      const seasonYearArray: ISelectOptions[] = seasonList.data.all().map((season) => ({
        title: season.title,
        value: season.id,
        secondaryValue: season,
      }));
      const currentTeamSeasonId = hostedCompetitionInfo.latestSeason?.id;
      const currentTeamSeasonIndex = findIndexByCondition(
        seasonYearArray,
        (option) => option.value === currentTeamSeasonId
      );
      const selectedOption = currentTeamSeasonIndex >= 0 ? seasonYearArray[currentTeamSeasonIndex] : seasonYearArray[0];
      setCurrentSeasonOption(selectedOption);
      if (selectedOption.value !== selectedGlobalSeasonOption.value) {
        dispatch(setGlobalSeasonSelectedOption(selectedOption));
      }
      setSeasonOptions(seasonYearArray);
    } else {
      setSeasonOptions([]);
    }
  }, [seasonList]);

  const handleEndConfirm = (e: Event) => {
    e.preventDefault();
    if (hostedCompetitionInfo && hostedCompetitionInfo.latestSeason) {
      setSeasonEnd(false);
      dispatch(
        seasonEnd({
          competitionId: hostedCompetitionInfo.id,
          seasonId: hostedCompetitionInfo.latestSeason?.id,
          seasonEndInfo: {
            endAt: formatDateTime(moment()),
          },
          urlParams: {
            hostTeamId: hostedCompetitionInfo.hostTeam.id,
          },
        })
      )
        .unwrap()
        .then(() => {
          addSnackbar({
            key: "season-end",
            text: "Season Ended Successfully",
            variant: "success",
          });
          dispatch(
            fetchManageHostedCompetition({
              teamId: hostedCompetitionInfo.hostTeam.id,
              hostedCompetitionId: hostedCompetitionInfo.id,
            })
          );
          dispatch(cleanUpSeasonFixtureGroups());
          dispatch(cleanUpSeasonTeamGroups());
          dispatch(cleanUpCompetitionSeasonTeams());
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  useEffect(() => {
    const url = location.pathname.split("/");
    setCurrentTab(url[url.length - 1]);
  }, [activeItem, location.pathname]);

  useEffect(() => {
    const lastPathSegment = location.pathname.split("/").pop();
    if (!isNaN(Number(lastPathSegment))) {
      setCurrentTab("profile");
    }
  }, [location.pathname]);

  const tabItems = [
    {
      label: "Profile",
      value: "profile",
      href: "profile",
    },
    {
      label: "Teams",
      value: "teams",
      href: "teams",
    },
    {
      label: "Events",
      value: "events",
      href: "events",
      hidden: ability.cannot(
        RolePolicyActionEnum.READ,
        new FootballMatchDetail({ competitionId: hostedCompetitionInfo.id })
      ),
    },
    {
      label: "Standings",
      value: "standings",
      href: "standings",
      hidden: hostedCompetitionInfo.type === TournamentTypeEnum.Knockout,
    },
  ];

  const handleSeasonChange = (option: ISelectOptions) => {
    setCurrentSeasonOption(option);
    dispatch(setGlobalSeasonSelectedOption(option));
  };

  return (
    <>
      <AppPaper shadow="xs" padding="none">
        {hostedCompetitionInfoStatus === StatusEnum.Loading ? (
          <AppSkeletonHeader withoutBackground />
        ) : (
          <>
            <AppBreadCrumbs px="md" py="2xs" crumbs={crumbs} />
            <AppBorderBox border={["Top", "Bottom"]}>
              <AppBox gap="sm" alignItems="center" justifyContent="space-between" p="sm">
                <AppBox alignItems="center" gap="xs">
                  <AppAvatar username={hostedCompetitionInfo.title} size="2xl" src={hostedCompetitionInfo.logo || ""} />
                  <AppBox flexDirection="column" gap="2xs">
                    <AppTitle as="h4">{hostedCompetitionInfo.title}</AppTitle>
                    {seasonOptions.length > 0 && (
                      <AppBox gap="2xs" alignItems="center">
                        <AppText>Season:</AppText>
                        <AppSelect
                          options={seasonOptions}
                          currentOption={currentSeasonOption}
                          smallSize
                          onChange={handleSeasonChange}
                        />

                        {(ability.can(
                          RolePolicyActionEnum.UPDATE,
                          new ManageFootballTeam(hostedCompetitionInfo.hostTeam)
                        ) ||
                          ability.can(
                            RolePolicyActionEnum.UPDATE,
                            new ManageFootballCompetition(hostedCompetitionInfo)
                          )) &&
                          currentSeasonOption &&
                          currentSeasonOption.secondaryValue.status === SeasonStatusEnum.Active && (
                            <AppIconButton
                              size="xs"
                              variant="light"
                              icon={<IconEdit />}
                              onClick={() => {
                                setSelectedSeason(currentSeasonOption.secondaryValue);
                                setSeasonStartEdit(true);
                                setDocumentTitle([`${hostedCompetitionInfo.abbreviation}`, "Edit Session", "Manage"]);
                              }}
                            />
                          )}
                      </AppBox>
                    )}
                  </AppBox>
                </AppBox>
                <AppBox gap="xs">
                  {(ability.can(RolePolicyActionEnum.UPDATE, new ManageFootballTeam(hostedCompetitionInfo.hostTeam)) ||
                      ability.can(RolePolicyActionEnum.UPDATE, new ManageFootballCompetition(hostedCompetitionInfo))) && (
                      <>
                        <AppButton variant="light" label="Edit Tournament Info" onClick={onEditTournament}/>
                        {!hostedCompetitionInfo?.latestSeason &&
                            <AppButton
                                variant="filled"
                                label="Start Season"
                                onClick={() => {
                                  setSeasonStartEdit(true);
                                  setDocumentTitle([`${hostedCompetitionInfo.abbreviation}`, "Start Session", "Manage"]);
                                }}
                            />
                        }
                        {hostedCompetitionInfo.latestSeason && (
                            <>
                              {hostedCompetitionInfo.latestSeason.status !== SeasonStatusEnum.Active && (
                                  <AppButton variant="filled" label="Start Season"
                                             onClick={() => setSeasonStartEdit(true)}/>
                              )}
                              {hostedCompetitionInfo.latestSeason.status === SeasonStatusEnum.Active &&
                                  currentSeasonOption &&
                                  currentSeasonOption.secondaryValue.status === SeasonStatusEnum.Active && (
                                      <AppButton variant="filled" label="End Season"
                                                 onClick={() => setSeasonEnd(true)}/>
                                  )}
                            </>
                        )}
                      </>
                  )}
                </AppBox>
              </AppBox>
            </AppBorderBox>
          </>
        )}
        <AppBox p="sm">
          <AppTabs
            as="a"
            onClick={(e) => {
              setActiveItem(e);
              window.scrollTo(0, 0);
            }}
            activeValue={currentTab}
            tabItems={tabItems}
          />
        </AppBox>
      </AppPaper>
      <AppModal
        title={selectedSeason ? "Edit Season" : "Start Season"}
        titleIcon={selectedSeason ? <IconEdit /> : <IconTrophy />}
        opened={isSeasonStartEdit}
        onClose={() => {
          setSeasonStartEdit(false);
          setDocumentTitle([`${hostedCompetitionInfo.abbreviation}`, "Manage"]);
        }}
        withoutButtons
      >
        <SeasonAddEdit
          teamInfo={hostedCompetitionInfo.hostTeam}
          competition={hostedCompetitionInfo}
          seasonData={selectedSeason}
          onSave={() => {
            setSeasonStartEdit(false);
            setSelectedSeason(null);
            setDocumentTitle([`${hostedCompetitionInfo.abbreviation}`, "Manage"]);
          }}
          onClose={() => {
            setSeasonStartEdit(false);
            setDocumentTitle([`${hostedCompetitionInfo.abbreviation}`, "Manage"]);
          }}
        />
      </AppModal>
      <AppModal
        opened={isSeasonEnd}
        onClose={(e) => {
          setSeasonEnd(false);
        }}
        title="Want To End This Season?"
        titleIcon={<IconQuestionCircle />}
        confirmButtonLabel="End"
        onSave={(e) => {
          handleEndConfirm(e as Event);
        }}
      >
        <SeasonEnd competition={hostedCompetitionInfo} />
      </AppModal>
      <AppModal
        opened={seasonEndResponseStatus === StatusEnum.Loading}
        title="Ending Season"
        titleIcon={<IconArrowSyncCircle />}
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <SeasonEndInprogress competition={hostedCompetitionInfo} />
      </AppModal>
    </>
  );
}
